import InputField from 'components/molecules/input-field'
import InputSelectOptions from 'components/molecules/input-select-options'
import LoaderButton from 'components/molecules/loader-button'
import {
  STATUS,
  COURIER,
  COURIER_DELIVERY_SERVICE,
  SELLER_SHIPMENT_TYPE,
  NON_SELLER_SHIPMENT_TYPE
} from 'domains/seller-outbound-return/constants'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect,useState } from 'react'
import { Row,Col } from 'reactstrap'
import sellerOutboundReturnAddressSchema from './seller-outbound-return-address-schema'
import {
  ProvinceSelectField,
  CitySelectField,
  DistrictSelectField,
  SubDistrictSelectField
} from 'components'


const propTypes = {
  seller               : PropTypes.object,
  sellerOutboundReturn : PropTypes.object,
  onSubmit             : PropTypes.func.isRequired
}

const defaultValue = {
  seller               : {},
  sellerOutboundReturn : {},
  onSubmit             : () => { }
}

/**
 * Form to update Seller Outbound Return address.
 * We named it as `...SetAddressFrom` since it used in two process
 * - Ship the outbound
 * - Edit the outbound address
 * @param {object} sellerOutboundReturn - selected Seller Outbound Return
 * @param {func} onSubmit - Handler for submitting the form
 */
const SellerOutboundReturnAddressForm = ({
  seller,
  sellerOutboundReturn,
  onSubmit
}) => {
  const [ shipmentTypeOptions, setShipmentTypeOptions ] = useState({})
  const addresses                                       = seller?.user?.addresses ?? []

  const getProvinceValue = (formikBag, provinceKey) => {
    const keys                     = provinceKey.split('.')
    const { values: formikValues } = formikBag
    let newValues                  = formikValues
    keys.map((k) => newValues = newValues[ k ])
    return newValues
  }

  useEffect(() => {
    handleChangeShipmentTypeOptions(sellerOutboundReturn.courier)
  }, [ sellerOutboundReturn ])

  // Add address data to reduce manual input
  let defaultAddress = addresses.find((address) => address.is_seller_default == true)
  defaultAddress     = defaultAddress || addresses[0]

  /**
   * Map outbound address to the initial value of the form
   * Ops can decide to use current Seller Outbound Address or Seller address
   * @param {object} sellerOutboundReturn - Address object
  */
  const mapInitialValue = (sellerOutboundReturn) => {
    let initialValues = {
      courier            : sellerOutboundReturn?.courier ?? '',
      shipment_type      : sellerOutboundReturn?.shipment_type ?? '',
      tracking_no        : sellerOutboundReturn?.tracking_no ?? '',
      street_address     : sellerOutboundReturn?.street_address ?? defaultAddress?.street_address ?? '',
      tl_province_id     : sellerOutboundReturn?.tl_province_id ?? defaultAddress?.tl_province.id ?? '',
      tl_city_id         : sellerOutboundReturn?.tl_city_id ?? defaultAddress?.tl_city.id ?? '',
      tl_district_id     : sellerOutboundReturn?.tl_district_id ?? defaultAddress?.tl_district.id ?? '',
      tl_sub_district_id : sellerOutboundReturn?.tl_sub_district_id ?? defaultAddress?.tl_sub_district.id ?? '',
      postal_code        : sellerOutboundReturn?.postal_code ?? defaultAddress?.postal_code ?? '',
      total_weight       : sellerOutboundReturn?.total_weight ?? 0
    }

    return initialValues
  }

  /**
   * Set seller address as outbound addresss.
   * Overwrite formik values with seller address
   */


  /**
   * Display/hide required label
   * @param {object} formikBag - Formik props
   */
  const displayRequiredLabel = (formikBag) => Object.values(COURIER_DELIVERY_SERVICE).includes(formikBag?.values?.courier)

  /**
   * Handler to change shipment type options depending on courier
   * @param {string} selectedCourier - selected courier at the form
   */
  const handleChangeShipmentTypeOptions = (selectedCourier) => {
    selectedCourier === COURIER.SELLER
      ? setShipmentTypeOptions(SELLER_SHIPMENT_TYPE)
      : setShipmentTypeOptions(NON_SELLER_SHIPMENT_TYPE)
  }

  return (
    <Formik
      enableReinitialize={ true }
      initialValues={ mapInitialValue(sellerOutboundReturn) }
      validationSchema={ sellerOutboundReturnAddressSchema }
      onSubmit={ (values, formikBag) => onSubmit(values, formikBag) }
    >
      {
        (formikBag) => (
          <Form>
            <Row>
              <Col
                md={ 12 }
                lg={ 6 }
              >
                <InputField
                  type='select'
                  name='courier'
                  label='Courier'
                  required={ true }
                  onChange= { (event) => {
                    formikBag.handleChange(event)
                    formikBag.setFieldValue('shipment_type', '')

                    handleChangeShipmentTypeOptions(event.target.value)
                  } }
                >
                  <InputSelectOptions options={ COURIER } keyPrefix={ 'courier' } />
                </InputField>

                <InputField
                  type='select'
                  name='shipment_type'
                  label='Shipment Type'
                  required={ true }
                >
                  <InputSelectOptions options={ shipmentTypeOptions } keyPrefix={ 'shipment-type' } />
                </InputField>

                <InputField
                  type='text'
                  name='tracking_no'
                  label='Tracking No.'
                  required={ displayRequiredLabel(formikBag) }
                />
                <InputField
                  type='text'
                  name='street_address'
                  label='Jalan'
                  required={ displayRequiredLabel(formikBag) }
                />
                <InputField
                  type='text'
                  name='total_weight'
                  label='Berat (Kg)'
                  required={ displayRequiredLabel(formikBag) }
                />
              </Col>
              <Col
                md={ 12 }
                lg={ 6 }
              >

                <ProvinceSelectField
                  label='Provinsi'
                  required={ displayRequiredLabel(formikBag) }
                  name='tl_province_id'
                />
                <CitySelectField
                  name='tl_city_id'
                  label='Kota'
                  required={ displayRequiredLabel(formikBag) }
                  provinceValue={ getProvinceValue(formikBag,'tl_province_id') }
                />
                <DistrictSelectField
                  name='tl_district_id'
                  label='Kecamatan'
                  required={ displayRequiredLabel(formikBag) }
                  formikBag={ formikBag }
                  provinceKey='tl_province_id'
                  cityKey='tl_city_id'
                />
                <SubDistrictSelectField
                  name='tl_sub_district_id'
                  label='Kelurahan'
                  required={ displayRequiredLabel(formikBag) }
                  formikBag={ formikBag }
                  provinceKey='tl_province_id'
                  cityKey='tl_city_id'
                  districtKey='tl_district_id'
                />
                <InputField
                  type='text'
                  name='postal_code'
                  label='Kode Pos'
                  required={ displayRequiredLabel(formikBag) }
                  disabled
                />
              </Col>
            </Row>

            <div className='d-flex justify-content-start mb-3 mr-3'>
              <LoaderButton
                color='primary'
                type='submit'
                onSubmit={ formikBag.submitForm }
                disabled={ formikBag.isSubmitting }
                isLoading={ formikBag.isSubmitting }
                text={ sellerOutboundReturn.status === STATUS.PENDING ? 'Ship' : 'Edit' }
              />
            </div>
          </Form>
        )
      }
    </Formik>
  )
}

SellerOutboundReturnAddressForm.propTypes    = propTypes
SellerOutboundReturnAddressForm.defaultProps = defaultValue

export default SellerOutboundReturnAddressForm
